import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import CircleBg from "@/components/atomics/circle-bg";
import SquareBg from "@/components/atomics/square-bg";
import Star from "@/components/atomics/star";
import MotionModal from "@/components/atomics/motion-modal";
import LeaderBoardRankBoost from "./leader-board-boost";
import LeaderBoardRankInfo from "./leader-board-rank-info";
import { LeaderboardMetric, MenuType } from "@/types/leader-board";
import {
  ROUTE_PATHS,
  METRIC_CONFIG,
  LEADERBOARD_CONSTANTS,
} from "@/constants/leaderboard";
import TruncateAddress from "@/components/utils/truncateAddress";

interface LeaderBoardTopProps {
  className?: string;
  bgColor?: string;
  image: string;
  name: string;
  rank: string;
  value: string;
  address: string;
  ownerAddress: string;
  selectedMenu: MenuType;
  metric: LeaderboardMetric;
  indexInCollection?: number;
  onRefetchData?: () => void;
}

export default function LeaderBoardTop({
  className = "",
  bgColor = "#000000",
  image,
  name,
  rank,
  value,
  selectedMenu,
  metric,
  indexInCollection,
  address,
  ownerAddress,
  onRefetchData,
}: LeaderBoardTopProps) {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);

  const handleImageClick = useCallback(() => {
    const routePath = ROUTE_PATHS[selectedMenu]?.(address);
    if (routePath) {
      navigate(routePath);
    }
  }, [navigate, selectedMenu, address]);

  const handleMetricClick = useCallback(() => {
    setShowModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const handleSendStar = useCallback((rating: number) => {
    // This is now handled internally by the LeaderBoardRankBoost component
    setShowModal(false);
  }, []);

  const renderImageSection = () => {
    if (selectedMenu === "users") {
      return (
        <div
          className="relative w-full hover:cursor-pointer"
          onClick={handleImageClick}
        >
          <CircleBg
            className="absolute translate-x-[-50%] left-[50%] top-[50%] translate-y-[-50%] w-[calc(100%+10px)] aspect-square"
            color={bgColor}
          />
          <div className="w-full aspect-square rounded-full bg-black">
            <img
              src={image}
              alt={`${name} avatar`}
              className="w-full aspect-square rounded-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
      );
    } else {
      return (
        <div
          className="relative w-full aspect-square hover:cursor-pointer"
          onClick={handleImageClick}
        >
          <SquareBg
            className="absolute translate-x-[-50%] left-[50%] top-[50%] translate-y-[-50%] w-[calc(100%+20px)] aspect-square"
            color={bgColor}
          />
          <div className="absolute top-[50%] translate-y-[-50%] left-[50%] translate-x-[-50%] w-full aspect-square bg-black">
            <img
              src={image}
              alt={`${name} collection`}
              className="w-full aspect-square object-cover"
              loading="lazy"
            />
          </div>
          {Array.from({ length: 4 }, (_, index) => (
            <div
              key={index}
              className={`absolute w-[5%] h-[5%] ${
                index === 0
                  ? "top-[-1px] left-[-1px]"
                  : index === 1
                    ? "top-[-1px] right-[-1px]"
                    : index === 2
                      ? "bottom-[-1px] left-[-1px]"
                      : "bottom-[-1px] right-[-1px]"
              }`}
              style={{ backgroundColor: bgColor }}
            />
          ))}
          {selectedMenu === "nft" && (
            <div
              className={`absolute left-[50%] translate-x-[-50%] bottom-[-8px] text-center text-[20px] w-[90%]`}
              style={{ backgroundColor: bgColor }}
            >
              #{indexInCollection?.toString()}
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  const renderMetricValue = () => {
    const config = METRIC_CONFIG[metric];
    if (!config) return null;

    const IconComponent = metric === "all_stars" ? Star : config.icon;

    return (
      <div
        className={`flex items-center gap-3 ${config.colorClass} hover:cursor-pointer hover:opacity-80 transition-opacity`}
        onClick={handleMetricClick}
      >
        <span className="text-[18px]">{value}</span>
        {IconComponent && (
          <IconComponent
            className={`w-[${LEADERBOARD_CONSTANTS.DEFAULT_AVATAR_SIZE}px] h-[${LEADERBOARD_CONSTANTS.DEFAULT_AVATAR_SIZE}px] text-primary-yellow`}
          />
        )}
        {config.suffix && (
          <span className="text-[20px] uppercase">{config.suffix}</span>
        )}
      </div>
    );
  };

  const userInfo = {
    rank: parseInt(rank),
    name,
    value: parseFloat(value),
    address,
    avatar: image,
    ownerAddress: address,
    indexInCollection,
  };

  return (
    <>
      <div
        className={`relative flex flex-col items-center transition-transform hover:scale-105 ${className}`}
      >
        {renderImageSection()}

        <div className="w-full flex flex-col items-center text-[20px] pt-[10px]">
          <TruncateAddress
            address={name}
            className="text-[20px] font-medium  max-w-full"
          />
          {renderMetricValue()}
          <span className="text-[18px] font-semibold">{rank} PLACE</span>
        </div>
      </div>

      <MotionModal isOpen={showModal}>
        {metric === "all_stars" ? (
          <LeaderBoardRankBoost
            userInfo={userInfo}
            selectedMenu={selectedMenu}
            metric={metric}
            onClickClose={handleCloseModal}
            onRefetchData={onRefetchData}
          />
        ) : (
          <LeaderBoardRankInfo
            userInfo={userInfo}
            selectedMenu={selectedMenu}
            metric={metric}
            onClickSendStar={handleSendStar}
            onClickClose={handleCloseModal}
          />
        )}
      </MotionModal>
    </>
  );
}
